import { FC, useEffect, useMemo, useState } from "react";
import { Avatar, Box, InputLabel, alpha, useTheme } from "@mui/material";
import { PersonSearch } from "@mui/icons-material";
import {
  MentionsInput,
  Mention,
  type MentionDataItem
} from "react-mentions-ts";
import { getAuthenticatedApiClient } from "../../../api/apiClient";
// Scoped Tailwind for this component only. See mentions.css for the @source
// scoping rules and read CLAUDE.md's "Mentions" section before adding new
// Tailwind classes here or elsewhere in the codebase.
import "./mentions.css";

// Two-letter initials from a display name, fallback to first char of the email.
const initialsOf = (display: string, email: string): string => {
  const parts = display.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (email.slice(0, 2) || "?").toUpperCase();
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  maxRows?: number;
  autoFocus?: boolean;
  fullWidth?: boolean;
};

// Tailwind classes as static strings so the JIT can definitely see them.
// Colors read from CSS custom properties we set at runtime from the MUI theme
// (below), so both light and dark modes render correctly.
//
// The library's own Tailwind defaults keep the highlighter and input in
// pixel-perfect metric + wrap + scroll sync - that was the point of the
// migration. Do NOT add glyph-width-affecting properties (font-weight,
// letter-spacing) to only one layer, and do NOT change the shared padding /
// font classes on one side without matching the other. The demo structure
// (border + overflow-hidden on the control wrapper, border-transparent on
// input and highlighter) is what makes the native textarea scroll work
// while the highlighter overlay stays clipped inside the same rounded box.
const CONTROL_CLASSES =
  "relative block w-full overflow-hidden rounded border border-[var(--mfa-border)] bg-[var(--mfa-bg)] transition focus-within:border-[var(--mfa-primary)] focus-within:ring-1 focus-within:ring-[var(--mfa-primary)]";
const SHARED_TYPE_AND_BOX =
  "box-border w-full border border-transparent px-3.5 py-3 text-sm leading-normal font-normal tracking-normal";
// Height cap on both layers is what makes the textarea's native scroll show
// up. Without it the highlighter grows with content and drags the whole
// wrapper taller - textarea's built-in overflow scroll never triggers because
// the wrapper is always big enough. Mirrored on the highlighter so the two
// stay the same shape (see Scrollable demo in the library repo).
const HIGHLIGHTER_CLASSES = `${SHARED_TYPE_AND_BOX} pointer-events-none whitespace-pre-wrap break-words text-transparent max-h-40 overflow-hidden`;
const INPUT_CLASSES = `${SHARED_TYPE_AND_BOX} block bg-transparent text-[var(--mfa-fg)] outline-none placeholder:text-[var(--mfa-placeholder)] max-h-40 overflow-y-auto resize-y`;
const SUGGESTIONS_CLASSES =
  "z-[1400] min-w-[260px] max-w-[380px] max-h-[280px] overflow-y-auto rounded-lg border border-[var(--mfa-popup-border)] bg-[var(--mfa-popup-bg)] shadow-xl py-1 mt-1";
const SUGGESTIONS_LIST_CLASSES = "m-0 list-none p-0 focus:outline-none";
const SUGGESTIONS_STATUS_CLASSES = "px-3 py-2 text-sm text-[var(--mfa-muted)]";
const SUGGESTION_ITEM_CLASSES = "cursor-pointer text-[var(--mfa-fg)]";
const LOADING_CLASSES = "flex items-center justify-center py-2";
const LOADING_SPINNER_CLASSES =
  "block h-4 w-4 rounded-full border-2 border-[var(--mfa-primary)]/25 border-t-[var(--mfa-primary)] animate-spin";
const LOADING_DOT_CLASSES = "hidden";

// Wraps react-mentions-ts to keep the highlighter/input alignment invariant
// (which is genuinely fiddly to reproduce by hand) in the library's own hands.
// The stored value carries react-mentions markup: @[Display Name](email). The
// backend worker parses those markers to email the mentioned users. Rendering
// back to plain text elsewhere goes through renderCommentText in commentUtils.
export const MentionInput: FC<Props> = ({
  value,
  onChange,
  label,
  placeholder,
  multiline = true,
  rows,
  maxRows,
  autoFocus = false,
  fullWidth = true
}) => {
  const theme = useTheme();

  // Bridge MUI palette into CSS custom properties the Tailwind class strings
  // above read via `var(--...)`. Static class strings + dynamic var values =
  // Tailwind JIT stays happy AND the component respects light/dark theming.
  const cssVars = useMemo(
    () =>
      ({
        "--mfa-fg": theme.palette.text.primary,
        "--mfa-bg": theme.palette.background.paper,
        "--mfa-border": theme.palette.divider,
        "--mfa-primary": theme.palette.primary.main,
        "--mfa-placeholder": theme.palette.text.disabled,
        "--mfa-muted": theme.palette.text.secondary,
        "--mfa-popup-bg": theme.palette.background.paper,
        "--mfa-popup-border": theme.palette.divider
      }) as React.CSSProperties,
    [theme]
  );

  const mentionStyle = useMemo(
    () => ({
      backgroundColor: alpha(theme.palette.primary.main, 0.16),
      color: theme.palette.primary.main
    }),
    [theme]
  );

  // Portal the suggestions list to document.body so MUI Dialog / small
  // containers don't clip the popup. Because portaled content is a DOM sibling
  // of the wrapper (not a descendant), the wrapper's CSS variables don't
  // inherit down - so we also mirror them onto document.body for the popup's
  // lifetime, and clean up on unmount.
  const [portalHost, setPortalHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    setPortalHost(document.body);
    const body = document.body;
    const applied: string[] = [];
    for (const [key, val] of Object.entries(cssVars)) {
      body.style.setProperty(key, String(val));
      applied.push(key);
    }
    return () => {
      for (const key of applied) {
        body.style.removeProperty(key);
      }
    };
  }, [cssVars]);

  // Sync-vs-async matters: an async function ALWAYS returns a Promise, and
  // the library shows the loading indicator for any Promise-returning data
  // source until it resolves. Returning [] synchronously for too-short
  // queries skips loading entirely and goes to the empty state.
  const fetchUsers = useMemo(
    () =>
      (query: string): MentionDataItem[] | Promise<MentionDataItem[]> => {
        const trimmed = query.trim();
        if (trimmed.length < 2) {
          return [];
        }
        return (async () => {
          const apiClient = await getAuthenticatedApiClient();
          const response = await apiClient.userSearch.userSearch({
            q: trimmed,
            top: 20
          });
          return (response.data ?? [])
            .filter((u) => !!u.email)
            .map((u) => ({
              id: u.email as string,
              display: u.name || u.email || ""
            }));
        })();
      },
    []
  );

  const classNames = useMemo(
    () => ({
      control: CONTROL_CLASSES,
      highlighter: HIGHLIGHTER_CLASSES,
      input: INPUT_CLASSES,
      suggestions: SUGGESTIONS_CLASSES,
      suggestionsList: SUGGESTIONS_LIST_CLASSES,
      suggestionsStatus: SUGGESTIONS_STATUS_CLASSES,
      suggestionItem: SUGGESTION_ITEM_CLASSES,
      loadingIndicator: LOADING_CLASSES,
      loadingSpinner: LOADING_SPINNER_CLASSES,
      loadingSpinnerElement: LOADING_DOT_CLASSES
    }),
    []
  );

  const renderSuggestion = useMemo(() => {
    const RenderSuggestion = (
      suggestion: MentionDataItem,
      query: string,
      _highlightedDisplay: unknown,
      _index: number,
      focused: boolean
    ) => {
      const display = suggestion.display ?? String(suggestion.id);
      const email = String(suggestion.id);
      const q = query.trim().toLowerCase();
      const lower = display.toLowerCase();
      const matchStart = q.length > 0 ? lower.indexOf(q) : -1;
      const matchEnd = matchStart >= 0 ? matchStart + q.length : -1;
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            paddingX: 1.25,
            paddingY: 0.75,
            backgroundColor: focused ? "action.hover" : "transparent"
          }}
        >
          <Avatar
            sx={{
              width: 28,
              height: 28,
              fontSize: "0.75rem",
              bgcolor: focused ? "primary.main" : "action.selected",
              color: focused ? "primary.contrastText" : "text.primary"
            }}
          >
            {initialsOf(display, email)}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                fontSize: "0.875rem",
                color: "text.primary",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {matchStart >= 0 ? (
                <>
                  {display.slice(0, matchStart)}
                  <Box
                    component="span"
                    sx={{ fontWeight: 700, color: "primary.main" }}
                  >
                    {display.slice(matchStart, matchEnd)}
                  </Box>
                  {display.slice(matchEnd)}
                </>
              ) : (
                display
              )}
            </Box>
            <Box
              sx={{
                fontSize: "0.75rem",
                color: "text.secondary",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {email}
            </Box>
          </Box>
        </Box>
      );
    };
    RenderSuggestion.displayName = "RenderSuggestion";
    return RenderSuggestion;
  }, []);

  const renderEmpty = useMemo(() => {
    const RenderEmpty = (query: string) => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          paddingX: 1.5,
          paddingY: 1.25,
          color: "text.secondary",
          fontSize: "0.8125rem"
        }}
      >
        <PersonSearch fontSize="small" />
        {query.trim().length < 2
          ? "Type at least 2 characters to search"
          : `No matches for "${query}"`}
      </Box>
    );
    RenderEmpty.displayName = "RenderEmpty";
    return RenderEmpty;
  }, []);

  const renderError = useMemo(() => {
    const RenderError = () => (
      <Box
        sx={{
          paddingX: 1.5,
          paddingY: 1.25,
          color: "error.main",
          fontSize: "0.8125rem"
        }}
      >
        Could not load users. Try again in a moment.
      </Box>
    );
    RenderError.displayName = "RenderError";
    return RenderError;
  }, []);

  return (
    <Box sx={{ width: fullWidth ? "100%" : undefined, ...cssVars }}>
      {label ? (
        <InputLabel shrink sx={{ marginBottom: 0.5 }}>
          {label}
        </InputLabel>
      ) : null}
      <MentionsInput
        value={value}
        onMentionsChange={({ value: next }) => onChange(next)}
        placeholder={placeholder}
        singleLine={!multiline}
        autoFocus={autoFocus}
        classNames={classNames}
        rows={multiline ? (rows ?? 3) : 1}
        suggestionsPortalHost={portalHost}
        {...(maxRows ? { maxRows } : {})}
      >
        <Mention
          trigger="@"
          markup="@[__display__](__id__)"
          data={fetchUsers}
          debounceMs={300}
          appendSpaceOnAdd
          renderSuggestion={renderSuggestion}
          renderEmpty={renderEmpty}
          renderError={renderError}
          // ONLY non-glyph-width-affecting properties here. Any width change
          // will drift against the plain text in the textarea above.
          style={mentionStyle}
        />
      </MentionsInput>
    </Box>
  );
};
